/* eslint-disable react/jsx-handler-names */
import PropTypes from "prop-types";
import React, { useLayoutEffect, useRef, useState } from "react";
import { WarningIcon } from "@phosphor-icons/react";

/**
 * Text field component with various customization options.
 * @param {object} props - The props for the component.
 * @param {string} props.type - The type of the input field.
 * @param {string} props.placeholder - The placeholder text for the input field.
 * @param {boolean} props.isRequired - Whether the input field is required or not.
 * @param {string} props.variant - The variant of the input field.
 * @param {string} props.id - The id of the input field.
 * @param {JSX.Element} props.endAdornment - The end adornment element for the input field.
 * @param {JSX.Element} props.startAdornment - The start adornment element for the input field.
 * @param {string} props.label - The label for the input field.
 * @param {string} props.inputValue - The value of the input field.
 * @param {boolean} props.isError - Whether the input field has an error or not.
 * @param {boolean} props.isSuccess - Whether the input field is successful or not.
 * @param {string} props.message - The message to display below the input field.
 * @param {boolean} props.isAutoComplete - Whether autocomplete is enabled for the input field.
 * @param {boolean} props.hasStartDivider - Whether to display a divider at the start of the input field.
 * @param {boolean} props.hasEndDivider -Whether to display a divider at the end of the input field.
 * @param {string} props.classes - Custom class for textfield input box.
 * @param {string} props.messageClass - Custom class for error, success message.
 * @returns {JSX.Element} The TextField component.
 */
const TextField = ({
  type,
  placeholder = "",
  isRequired = false,
  variant = "primary",
  id,
  endAdornment = null,
  startAdornment = null,
  label = "",
  inputValue = "",
  isError = false,
  isSuccess = false,
  message = "",
  hasStartDivider = false,
  hasEndDivider = false,
  isAutoComplete = false,
  classes = "",
  messageClass = "",
  customRef = null,
  isFromPDP = false,
  isErrorMsgAnimate = false,
  labelParentClass = "",
  isLabelCentered = false,
  isFromReminder = false,
  isFromCheckout = false,
  endAdornmentPadding = "pr-[75px]",
  isAutoGrowMultiline = false,
  maxRows = 2,
  isScrollableMultiline = false,
  ...restParams
}) => {
  const textAreaRef = useRef(null);
  const baseHeightRef = useRef(0);
  const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);
  // Scrollable mode: past maxRows the textarea scrolls (focused or not) instead of showing a truncated view on blur.
  const isTruncatedViewVisible =
    isAutoGrowMultiline && !isScrollableMultiline && !isTextAreaFocused && Boolean(inputValue);

  /**
   * Auto-grow mode: animates textarea height between 1 and maxRows lines as the value wraps.
   */
  useLayoutEffect(() => {
    const element = textAreaRef.current;
    if (!isAutoGrowMultiline || !element) return;

    const styles = window.getComputedStyle(element);
    const lineHeight = parseFloat(styles.lineHeight) || 20;
    const verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    if (!baseHeightRef.current) {
      baseHeightRef.current = element.offsetHeight;
    }

    // Tailwind runs with `important: true`, so height classes are !important; inline height must be too.
    /**
     * @param {string} value - CSS height value.
     */
    const setHeight = (value) => element.style.setProperty("height", value, "important");
    const previousHeight = element.style.height || `${element.offsetHeight}px`;
    setHeight("auto");
    const lines = Math.max(1, Math.round((element.scrollHeight - verticalPadding) / lineHeight));
    const targetHeight =
      lines <= 1 ? baseHeightRef.current : baseHeightRef.current + (Math.min(lines, maxRows) - 1) * lineHeight;
    setHeight(previousHeight);
    // Force reflow so the height change below animates from the previous height.
    // eslint-disable-next-line no-unused-expressions
    element.offsetHeight;
    setHeight(`${targetHeight}px`);
  }, [inputValue, isAutoGrowMultiline, maxRows]);

  /**
   * Sets both the internal auto-grow ref and the caller's ref.
   * @param {HTMLElement} node
   */
  const setTextAreaRef = (node) => {
    textAreaRef.current = node;
    if (typeof customRef === "function") customRef(node);
    else if (customRef) customRef.current = node;
  };

  /**
   * Keeps the auto-grow field single-valued (no line breaks on Enter).
   * @param {KeyboardEvent} event
   */
  const onTextAreaKeyDown = (event) => {
    if (event.key === "Enter") event.preventDefault();
    restParams.onKeyDown?.(event);
  };

  /**
   * Shows the first lines again (truncated view) when the field loses focus.
   * @param {FocusEvent} event
   */
  const onTextAreaBlur = (event) => {
    if (!isScrollableMultiline) event.target.scrollTop = 0;
    setIsTextAreaFocused(false);
    restParams.onBlur?.(event);
  };

  /**
   * Switches back to the editable view when the field gets focus.
   * @param {FocusEvent} event
   */
  const onTextAreaFocus = (event) => {
    setIsTextAreaFocused(true);
    restParams.onFocus?.(event);
  };
  /**
   *  Receieve input type name as param.
   *  @param {string} inputType
   *  @returns {boolean} retrun true or false.
   */
  const checkInputType = (inputType) => {
    const nonTextTypes = ["radio", "checkbox", "submit"];
    return nonTextTypes.includes(inputType) ? "text" : inputType;
  };

  const variantClasses = {
    primary: `focus:border-oliveBlack border-greySoft rounded-lg border text-fnp-500 text-sm block  focus:outline-none w-full p-3.5 h-44 md:h-auto placeholder-grey-400`,
    primaryWithDarkBorder:
      "rounded-lg border border-grey-500 text-fnp-500 text-sm block focus:outline-none w-full p-3.5 h-44 md:h-auto",
    withComponent:
      "rounded-lg border border-greySoft text-fnp-500 text-sm block  focus:outline-none w-full p-3.5 h-44 md:h-auto",
    "outline-none":
      "rounded-lg text-fnp-500 text-sm  placeholder-grey-400 focus:outline-none w-full p-3.5 h-44 md:h-auto",
  };

  const labelPositionClass = startAdornment ? "left-44" : "left-16";
  // Floated label when the field has a value, set on the label itself: some iOS Safari versions do not restyle
  // the peer-data-[te-input-state-active] sibling selector when the attribute appears, so the label overlapped
  // prefilled / autofilled values.
  const isLabelActive = Boolean(label && inputValue);
  const labelOffsetClass = isLabelActive
    ? `${isLabelCentered ? "top-1/2" : "top-1"} -translate-y-[0.5rem] scale-[0.7] bg-white-900 py-0 ${
        startAdornment ? "-translate-x-[0.1rem]" : ""
      }`
    : isLabelCentered
      ? "top-1/2 -translate-y-1/2 pt-0"
      : "top-1 pt-[0.75rem] peer-focus:-translate-y-[0.5rem] peer-focus:scale-[0.7] peer-focus:py-0";
  return (
    <div className="relative w-full">
      {isError && message && !isFromReminder && !isFromCheckout && (
        <div
          className={`absolute top-0  h-[44px] rounded-lg md:h-[50px] ${isFromPDP ? "-left-[76px] w-[calc(100%+76px)] xl:-left-[80px] xl:w-[calc(100%+80px)]" : "left-0 w-full"}`}
        />
      )}
      <div className="group/textfield relative">
        {startAdornment && (
          <div
            className={`absolute inset-y-0 left-0 flex items-center pl-[12px] pr-[12px]  ${variant === "withComponent" ? "min-w-88 max-w-88" : ""} ${startAdornment?.props?.onClick ? "cursor-pointer" : "pointer-events-none"}`}
            onClick={startAdornment?.props?.onClick ?? null}
          >
            {hasStartDivider && (
              <div
                data-testid="start_divider"
                className={`absolute right-1 ${variant === "withComponent" ? "top-0" : "top-2"} ${variant === "withComponent" ? "bottom-0" : "bottom-2"} w-px self-stretch border-t-0 ${isError ? "bg-red-100" : "bg-grey-300"} ${
                  isError && variant === "primary" ? "group-focus-within/textfield:bg-fnp-500" : ""
                }  lg:block dark:opacity-100`}
              />
            )}
            {startAdornment}
          </div>
        )}
        {isAutoGrowMultiline ? (
          <textarea
            aria-label={id}
            data-testid="input_field"
            rows={1}
            className={` ${classes} inter ${variantClasses[variant]} ${endAdornment ? endAdornmentPadding : ""} ${startAdornment ? "pl-11" : ""} ${
              variant === "withComponent" ? "pl-[6rem]" : ""
            } ${isError ? "border-red" : ""} ${label ? "peer" : ""} ${isTruncatedViewVisible ? "[&&]:text-transparent" : ""} resize-none ${isScrollableMultiline ? "scrollbar-hide overflow-y-auto" : "overflow-hidden"} whitespace-pre-wrap break-words transition-[height] duration-200 ease-in-out disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-white-900`}
            placeholder={label ? "" : placeholder}
            required={isRequired}
            id={id}
            autoComplete={isAutoComplete ? "on" : "off"}
            value={inputValue}
            ref={setTextAreaRef}
            {...(inputValue && label ? { "data-te-input-state-active": true } : {})}
            {...restParams}
            onKeyDown={onTextAreaKeyDown}
            onBlur={onTextAreaBlur}
            onFocus={onTextAreaFocus}
          />
        ) : (
          <input
            aria-label={id}
            data-testid="input_field"
            type={checkInputType(type)}
            className={` ${classes} inter ${variantClasses[variant]} ${endAdornment ? endAdornmentPadding : ""} ${startAdornment ? "pl-11" : ""} ${
              variant === "withComponent" ? "pl-[6rem]" : ""
            } ${isError ? "border-red" : ""} ${label ? "peer" : ""}  overflow-hidden text-ellipsis whitespace-nowrap disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-white-900`}
            placeholder={label ? "" : placeholder}
            required={isRequired}
            id={id}
            autoComplete={isAutoComplete ? "on" : "off"}
            value={inputValue}
            ref={customRef}
            {...(inputValue && label ? { "data-te-input-state-active": true } : {})}
            {...restParams}
          />
        )}
        {isTruncatedViewVisible ? (
          // Blurred auto-grow field: show value clamped to maxRows lines with an ellipsis.
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 border border-transparent p-3.5 text-sm text-fnp-500 ${
              endAdornment ? endAdornmentPadding : ""
            } ${startAdornment ? "pl-11" : ""}`}
          >
            <p
              className="inter overflow-hidden break-words"
              style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: maxRows }}
            >
              {inputValue}
            </p>
          </div>
        ) : null}
        {label && (
          <label
            data-testid="text_field_label"
            htmlFor={id}
            className={`pointer-events-none absolute mb-0 max-w-[90%] origin-[0_0] truncate text-sm leading-[1.6] peer-focus:bg-white-900 ${labelOffsetClass} ${
              isError && !isFromReminder ? (isFromCheckout ? "text-grey-500" : "text-red-100") : "text-grey-500"
            } ${labelParentClass} peer-focus:text-primary transition-all duration-200 ease-out peer-focus:bg-white-900 peer-data-[te-input-state-active]:-translate-y-[0.5rem] peer-data-[te-input-state-active]:scale-[0.7] peer-data-[te-input-state-active]:bg-white-900 peer-data-[te-input-state-active]:py-0 motion-reduce:transition-none ${labelPositionClass} ${variant === "withComponent" ? "left-[6rem]" : ""} ${
              startAdornment
                ? "peer-focus:-translate-x-[0.1rem] peer-data-[te-input-state-active]:-translate-x-[0.1rem]"
                : ""
            }`}
          >
            {label}
          </label>
        )}
        {endAdornment && (
          <div
            className={`absolute inset-y-0 right-0 flex items-center pl-[6px] pr-[12px] ${
              endAdornment.props.onClick ? "cursor-pointer" : "pointer-events-none"
            }`}
            onClick={endAdornment?.props?.onClick ?? null}
          >
            {hasEndDivider && (
              <div
                data-testid="end_divider"
                className="absolute bottom-8 left-0 top-8 w-[1.5px] self-stretch border-t-0 bg-[#EEE] lg:block"
              />
            )}
            {endAdornment}
          </div>
        )}
      </div>
      {message && (isSuccess || isError) && (
        <span
          data-testid="input_related_message"
          className={`mt-10 flex ${isErrorMsgAnimate ? "animate-shake" : ""} rounded-4 text-sm font-500 tracking-wide text-grey-500 ${
            isError ? "text-12 leading-16 text-red-100" : ""
          } ${isSuccess ? "bg-fnp-200 bg-opacity-5 font-600 text-fnp-500" : ""} ${messageClass}`}
        >
          {isError && (
            <div className="w-16">
              <WarningIcon size={16} weight="fill" />
            </div>
          )}
          <p className="ml-4">{message}</p>
        </span>
      )}
    </div>
  );
};
TextField.propTypes = {
  type: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  variant: PropTypes.oneOf(["primary", "withComponent", "outline-none", "primaryWithDarkBorder"]),
  id: PropTypes.string.isRequired,
  endAdornment: PropTypes.node,
  startAdornment: PropTypes.node,
  label: PropTypes.string,
  inputValue: PropTypes.string,
  isError: PropTypes.bool,
  isSuccess: PropTypes.bool,
  message: PropTypes.string,
  hasStartDivider: PropTypes.bool,
  hasEndDivider: PropTypes.bool,
  isAutoComplete: PropTypes.bool,
  placeholder: PropTypes.string,
  classes: PropTypes.string,
  messageClass: PropTypes.string,
  customRef: PropTypes.any,
  isFromPDP: PropTypes.bool,
  isErrorMsgAnimate: PropTypes.bool,
  labelParentClass: PropTypes.string,
  isLabelCentered: PropTypes.bool,
  isFromReminder: PropTypes.bool,
  isFromCheckout: PropTypes.bool,
  endAdornmentPadding: PropTypes.string,
  isAutoGrowMultiline: PropTypes.bool,
  maxRows: PropTypes.number,
  isScrollableMultiline: PropTypes.bool,
};

export default React.memo(TextField);
